import { useCallback, useEffect, useRef } from 'react';

import { useExplorationData } from '../context/ExplorationContext';
import { useInert } from '../hooks/useInert';
import ExplorationBreadcrumb from './ExplorationBreadcrumb';
import ExplorationCanvasFilters from './ExplorationCanvasFilters';
import {
  explorationBreadcrumbFilterGapPx,
  explorationCanvasTopChromeClass,
  explorationCanvasWrapId,
  explorationFilterContainerMaxWidthStyle,
  explorationFilterSlotAttr,
  syncExplorationBreadcrumbWidth,
} from './explorationCanvasChrome';

interface ExplorationCanvasTopChromeProps {
  showBreadcrumb: boolean;
}

export default function ExplorationCanvasTopChrome({
  showBreadcrumb,
}: ExplorationCanvasTopChromeProps) {
  const { loading } = useExplorationData();
  const chromeRef = useRef<HTMLDivElement>(null);
  const breadcrumbRef = useRef<HTMLElement>(null);
  const lastBreadcrumbWidthRef = useRef(0);
  useInert(chromeRef, loading);
  const updateBreadcrumbWidthVar = useCallback(() => {
    const canvas = document.getElementById(explorationCanvasWrapId);
    const breadcrumb = breadcrumbRef.current;
    if (!breadcrumb || !showBreadcrumb) {
      syncExplorationBreadcrumbWidth(canvas, 0);
      return;
    }

    const width = breadcrumb.getBoundingClientRect().width;
    if (width > 0) {
      lastBreadcrumbWidthRef.current = width;
    }
    syncExplorationBreadcrumbWidth(canvas, lastBreadcrumbWidthRef.current);
  }, [showBreadcrumb]);

  useEffect(() => {
    const breadcrumb = breadcrumbRef.current;
    const canvas = document.getElementById(explorationCanvasWrapId);

    if (!showBreadcrumb) {
      syncExplorationBreadcrumbWidth(canvas, 0);
      return;
    }

    if (!breadcrumb) return;

    updateBreadcrumbWidthVar();
    const observer = new ResizeObserver(updateBreadcrumbWidthVar);
    observer.observe(breadcrumb);
    return () => observer.disconnect();
  }, [showBreadcrumb, updateBreadcrumbWidthVar]);

  return (
    <div ref={chromeRef} className={explorationCanvasTopChromeClass}>
      {showBreadcrumb ? <ExplorationBreadcrumb ref={breadcrumbRef} /> : null}
      {showBreadcrumb ? (
        <div
          className="shrink-0"
          style={{ width: explorationBreadcrumbFilterGapPx }}
          aria-hidden
        />
      ) : null}
      <div
        {...{ [explorationFilterSlotAttr]: '' }}
        className="ml-auto flex min-w-0 flex-1 justify-end"
        style={showBreadcrumb ? explorationFilterContainerMaxWidthStyle() : undefined}
      >
        <ExplorationCanvasFilters />
      </div>
    </div>
  );
}
